<template>
  <div>
    <div class="row">
      <div class="col-sm-2">
        <OverviewBox title="Credit Note" avatarImage="/glass/img/accounts-overview-creditnote.png" :avatarCircleMode="false" :loading="pageDataLoading">
          <h4>Credit Note #{{creditNote.sequenceNumber}}</h4>

          <template v-if="creditNote.labelList && creditNote.labelList.length > 0">
            <h5>Labels</h5>
            <p>
              <span class="label label-empty label-overview-box mr-1" v-for="label in creditNote.labelList" :key="label.id"><i class="fas fa-circle pr-1" :style="{color: label.colour}"> </i> {{label.name}}</span>
            </p>
          </template>
          <template v-if="hasIntegrations">
            <h5>Integrations</h5>
            <p>
              <LinkIntegrationLink v-for="integration in integrationList" :key="integration.code" :integration="integration" />
            </p>
          </template>

          <OverviewBoxInfoItem v-if="creditNote.contact" icon="fa-user-circle">
            <LinkContact :id="creditNote.contactId" :name="creditNote.contact.name" />
          </OverviewBoxInfoItem>
          <OverviewBoxInfoItem v-if="creditNote.order" icon="fa-shopping-cart">
            <LinkOrder :id="creditNote.orderId" :name="creditNote.order.sequenceNumber" />
          </OverviewBoxInfoItem>

          <OverviewBoxInfoItem icon="fa-tag" v-if="creditNote.description">
            {{creditNote.description}}
          </OverviewBoxInfoItem>
          <OverviewBoxInfoItem icon="fa-usd-circle">
            {{creditNote.totalCredit | formatCurrency}}
          </OverviewBoxInfoItem>
          <OverviewBoxInfoItem icon="fa-usd-circle" v-if="availableAllocationAmount > 0">
            <span class="financial-profit">{{availableAllocationAmount | formatCurrency}} Available</span>
          </OverviewBoxInfoItem>
          
          <OverviewBoxList>
            <OverviewBoxListItem title="Status">
              <LabelBadge :labelClass="`label-creditnote-status-${creditNote.status}`">{{creditNote.status | formatLookupName(CreditNoteStatusTypeLookup)}}</LabelBadge>
            </OverviewBoxListItem>
            <OverviewBoxListItem title="Number" v-if="creditNote.sequenceNumber">{{creditNote.sequenceNumber}}</OverviewBoxListItem>
            <OverviewBoxListItem title="Created On">{{creditNote.createdAt | formatDate}}</OverviewBoxListItem>
            <OverviewBoxListItem title="Last Updated">{{creditNote.updatedAt | formatDate}}</OverviewBoxListItem>
          </OverviewBoxList>
        </OverviewBox>

      </div>

      <div class="col-sm-10">
        <TabbedBox ref="tabBox" :loading="pageDataLoading">
          <template v-slot:tools>
            
          </template>

          <template v-slot:tablist>
            <Tab :first="true" tabid="tab-creditnote-summary">
              <i class="far fa-tasks"></i> Summary
            </Tab>
            <Tab tabid="tab-creditnote-activitylog">
              <i class="far fa-history"></i> Activity Log
            </Tab>
          </template>

          <template v-slot:tabcontent>
            <TabPage :first="true" tabid="tab-creditnote-summary">
              <div v-if="creditNote.id">
                <DataPageArea title="Details">
                  <template slot="actionArea">
                    <ActionDropMenu class="float-right">
                      <DropdownMenuItem v-if="userHasUpdatePermission(PermissionTypes.AccountsPermissionTypes.creditNote) && availableAllocationAmount > 0 && !isCreditNoteCancelled" @click="allocateCreditNote" icon="fa-plus">Allocate Credit</DropdownMenuItem>
                      <DropdownMenuItem @click="closeCreditNote()" icon="fa-check-circle" v-if="isCreditNoteClosable">Close Credit Note</DropdownMenuItem>
                      <DropdownMenuItem @click="cancelCreditNote" icon="fa-times" v-if="canCancelCreditNote">Cancel Credit Note</DropdownMenuItem>
                      <DropdownMenuDivider v-if="(userHasUpdatePermission(PermissionTypes.AccountsPermissionTypes.creditNote) && availableAllocationAmount > 0 && !isCreditNoteCancelled) || canCancelCreditNote" />
                      <DropdownMenuItem @click="sendCreditNoteReceipt" icon="fa-envelope">Send Credit Note Receipt</DropdownMenuItem>
                      <DropdownMenuItem @click="displayCreditNoteReport()" icon="fa-print">Credit Note Receipt</DropdownMenuItem>
                    </ActionDropMenu>
                  </template>
                  <template slot="dataArea">
                    <DataItem title="Payment Number">{{creditNote.sequenceNumber}}</DataItem>
                    <DataItem title="Description">{{creditNote.description}}</DataItem>
                    <DataItem title="Total Credit">{{creditNote.totalCredit | formatCurrency}}</DataItem>
                    <DataItem title="Credit Available">
                      <span :class="{'financial-profit': availableAllocationAmount > 0}">{{availableAllocationAmount | formatCurrency}}</span>
                    </DataItem>
                    <DataItem title="Date Created">{{creditNote.createdAt | formatDateTime}}</DataItem>
                  </template>
                </DataPageArea>

                <div class="row mt-4">
                  <div class="col-12">
                    <ContentTitle>Order Reserved Credits</ContentTitle>
                    <ContentSubTitle>Orders this credit note is reserved against pending invoicing</ContentSubTitle>
                    <StaticDataTable class="static-table-list">
                      <template v-slot:header>
                        <tr>
                          <th>Order</th>
                          <th>Order Status </th>
                          <th>Reserved Date</th>
                          <th>Order Amount</th>
                          <th>Reserved Amount</th>
                        </tr>
                      </template>
                      <template v-slot:body>
                        <tr v-for="reservedPayment in creditNote.orderReservedPayments" :key="reservedPayment.id">
                          <td class="font-weight-bold">
                            <LinkOrder :id="reservedPayment.orderId" :name="reservedPayment.order.sequenceNumber" />
                          </td>
                          <td>
                            <LabelBadge :labelClass="`label-order-status-${reservedPayment.order.status}`">{{reservedPayment.order.status | formatLookupName(OrderStatusTypeLookup)}}</LabelBadge>
                          </td>
                          <td>{{reservedPayment.dateReserved | formatDateTime}}</td>
                          <td>{{reservedPayment.order.totalPrice | formatCurrency}}</td>
                          <td>{{reservedPayment.reservedAmount | formatCurrency}}</td>
                        </tr>
                      </template>
                      <template v-slot:empty v-if="!hasOrderReservedPayments">
                        No credits reserved for orders
                      </template>
                    </StaticDataTable>
                  </div>
                </div>

                <div class="row mt-4">
                  <div class="col-12">
                    <ContentTitle>Invoice Allocations</ContentTitle>
                    <ContentSubTitle>Invoices this credit note has been allocated to</ContentSubTitle>
                    <StaticDataTable class="static-table-list">
                      <template v-slot:header>
                        <tr>
                          <th>Invoice</th>
                          <th>Invoice Status </th>
                          <th>Payment Status</th>
                          <th>Payment Date</th>
                          <th>Invoice Amount</th>
                          <th>Payment Amount</th>
                        </tr>
                      </template>
                      <template v-slot:body>
                        <tr v-for="invoicePayment in creditNote.invoicePaymentAllocations" :key="invoicePayment.id">
                          <td class="font-weight-bold">
                            <LinkInvoice :id="invoicePayment.invoiceId" :name="invoicePayment.invoice.sequenceNumber" />
                          </td>
                          <td>
                            <LabelBadge :labelClass="`label-invoice-status-${invoicePayment.invoice.status}`">{{invoicePayment.invoice.status | formatLookupName(InvoiceStatusTypeLookup)}}</LabelBadge>
                          </td>
                          <td>
                            <LabelBadge :labelClass="`label-payment-status-${invoicePayment.status}`">{{invoicePayment.status | formatLookupName(PaymentStatusTypeLookup)}}</LabelBadge>
                          </td>
                          <td>{{invoicePayment.dateAllocated | formatDateTime}}</td>
                          <td>{{invoicePayment.invoice.totalPrice | formatCurrency}}</td>
                          <td class="font-weight-bold">{{invoicePayment.allocatedAmount | formatCurrency}}</td>
                        </tr>
                      </template>
                      <template v-slot:empty v-if="!hasInvoicePaymentAllocations">
                        No invoice allocations made
                      </template>
                    </StaticDataTable>
                  </div>
                </div>
              </div>
            </TabPage>
            
            <TabPage tabid="tab-creditnote-activitylog">
              <div class="row">
                <div class="col-lg-12">
                  <ActivityLogStream ref="activityStream" :url="activityStreamURL" :linkType="LinkTypes.creditNote" :linkId="creditNote.id">
                    <template v-slot:title><h3>Recent Activity</h3></template>
                  </ActivityLogStream>
                </div>
              </div>
            </TabPage>
          </template>
        </TabbedBox>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import form from '@/mixins/form'
import Utils from '@/utils/utils'
import pdfMixin from '@/mixins/pdf'

import CreditNoteAllocationDialog from '@/modules/accounts/views/creditNoteAllocationDialog'
import SendCreditNoteDialog from '@/modules/accounts/views/sendCreditNoteDialog'
import CreditNoteCancelDialog from '@/modules/accounts/views/creditNoteCancelDialog'
import CreditNoteCloseDialog from '@/modules/accounts/views/creditNoteCloseDialog'

import { OrderStatusTypeLookup, InvoiceStatusTypeLookup, PaymentStatusTypeLookup, CreditNoteStatusTypes, CreditNoteStatusTypeLookup, LinkTypes } from '@/store/types'


export default {
  name: 'CreditNote',
  mixins: [form, pdfMixin],
  computed: {
    activityStreamURL() {
      if(!this.$data.creditNoteId)
        return null
      return `/accounts/creditnotes/${this.$data.creditNoteId}/activitystream`
    },
    hasIntegrations() {
      return this.integrationList.filter(integration => integration.active).length > 0
    },
    integrationList() {
      if(this.$data.creditNote.integrationData == null)
        return []
      return Object.values(this.$data.creditNote.integrationData)        
    },
    hasInvoicePaymentAllocations() {
      if(this.$data.creditNote.invoicePaymentAllocations == null)
        return false
      return this.$data.creditNote.invoicePaymentAllocations.length > 0
    },
    hasOrderReservedPayments() {
      if(this.$data.creditNote.orderReservedPayments == null)
        return false
      return this.$data.creditNote.orderReservedPayments.length > 0
    },
    isCreditNoteCancelled() {
      return this.creditNote.status == CreditNoteStatusTypes.cancelled
    },
    canCancelCreditNote() {
      if(!this.userHasUpdatePermission(this.PermissionTypes.AccountsPermissionTypes.creditNote))
        return false
      if(this.creditNote.status == CreditNoteStatusTypes.cancelled)
        return false
      return true
    },
    isCreditNoteClosable() {
      if(this.creditNote.status == CreditNoteStatusTypes.available && this.userHasUpdatePermission(this.PermissionTypes.AccountsPermissionTypes.accountAdministrator))
        return true
      return false
    },
    availableAllocationAmount() {
      return this.creditNote.availableAmount - this.creditNote.totalReservedAmount
    }
  },
  data() {
    return {
      creditNoteId: null,
      creditNote: {}
    }
  },
  created() {
    this.LinkTypes = LinkTypes
    this.OrderStatusTypeLookup = OrderStatusTypeLookup
    this.InvoiceStatusTypeLookup = InvoiceStatusTypeLookup
    this.PaymentStatusTypeLookup = PaymentStatusTypeLookup
    this.CreditNoteStatusTypes = CreditNoteStatusTypes
    this.CreditNoteStatusTypeLookup = CreditNoteStatusTypeLookup
  },
  mounted() {
    this.$data.creditNoteId = this.$route.params.id

    // Load the credit note
    this.requestData()
  },
  beforeRouteUpdate (to, from, next) {
    next()
    this.$data.creditNoteId = this.$route.params.id

    this.pageRefreshed()
    this.requestData()
  },
  methods: {
    reloadData() {
      this.$refs.activityStream.reloadData()
      this.requestData()
    },
    requestData() {
      this.pageLoadData(Promise.all([
        this.$apiService.accountsService.getCreditNote(this.$data.creditNoteId),
      ]))
      .then(results => {
        this.creditNote = results[0].data
      })
      .catch(err => this.handlePageLoadError(err))
    },
    allocateCreditNote() {
      this.displayModal(CreditNoteAllocationDialog, {
        creditNoteId: parseInt(this.$data.creditNoteId)
      })
      .then((success) => {
        if(success) {
          this.successToast('Credits Allocated')
          this.reloadData()
        }
      })      
    },
    sendCreditNoteReceipt() {
      this.displayModal(SendCreditNoteDialog, { 
        creditNoteId: parseInt(this.$data.creditNoteId),
        contactId: parseInt(this.creditNote.contactId)
      })
      .then((success) => {
        if(success) {
          this.successToast('Receipt Sent')
          this.reloadData()
        }
      })
    },
    displayCreditNoteReport(options = {}) {
      var params = $.param(options)
      this.displayReportPDF(`/accounts/creditnotes/${this.$data.creditNoteId}/report?${params}`)
    },
    cancelCreditNote() {
      this.displayModal(CreditNoteCancelDialog, { 
        creditNoteId: parseInt(this.$data.creditNoteId)
      })
      .then((success) => {
        if(success) {
          this.successToast('Credit Note Cancelled')
          this.reloadData()
        }
      })
    },
    closeCreditNote() {
      this.displayModal(CreditNoteCloseDialog, {
        creditNoteId: parseInt(this.$data.creditNoteId)
      })
      .then((success) => {
        if(success) {
          this.successToast('Credit Note Updated')
          this.reloadData()
        }
      })
    }
  }
}
</script>
